import { message, Modal } from 'antd'
import { ModalProps } from 'antd/lib/modal'
import {
    Form as FormikForm,
    Formik,
    FormikHelpers,
    FormikErrors,
    FormikProps
} from 'formik'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { Buttons } from './Buttons'
import { AlignLeftRight } from '../layout/Align'
import * as styles from './Form.module.less'

export interface FormValues {
    [key: string]: any
}

export interface FormProps {
    getFormikBag?: (formikBag: FormikProps<FormValues>) => void
    errorText?: string
    form: React.ReactElement
    formClassName?: string
    formContainerClassName?: string
    formSideColor?: string
    formTitle?: React.ReactNode
    modalFormClassName?: string
    getButtons?: (buttons: {
        cancelButton: React.ReactNode
        resetButton: React.ReactNode
        submitButton: React.ReactNode
    }) => void
    initialValues?: FormValues
    resetButtonName?: string
    submit?: (values: FormValues) => Promise<FormikErrors<FormValues> | any>
    submitButtonName?: string
    successText?: string
    validationSchema?: any
    getFormValues?: (values: FormValues) => void
    onCancel?: () => void
    showCancelButton?: boolean
    showButtons?: boolean
    showErrorMessage?: boolean
    validateOnChange?: boolean

    modal?: boolean
    modalProps?: ModalProps
    isVisible?: boolean
    hide?: () => void
}

/**
 * Form that can appear in a modal or directly.
 */
interface FormTemplate {
    buttons?: React.ReactElement
    form: React.ReactElement
    formTitle?: React.ReactNode
    isSubmitting: boolean
}
const FormTemplate: React.FC<FormTemplate> = (props) => {
    const { buttons, form, formTitle } = props
    return (
        <FormikForm>
            {formTitle}
            {form}
            <AlignLeftRight left={undefined} right={buttons} />
        </FormikForm>
    )
}

/**
 * Show a form in a modal or directly.
 */
export const Form: React.FC<FormProps> = React.memo((props) => {
    const [formValues, setFormValues] = useState<FormValues>({})

    const {
        errorText,
        form,
        formClassName,
        formContainerClassName,
        formSideColor,
        formTitle,
        getButtons,
        getFormValues,
        modalFormClassName,
        modalProps,
        onCancel,
        resetButtonName,
        submit,
        submitButtonName,
        successText,
        validationSchema,

        modal,
        isVisible,
        hide
    } = props

    const validateOnChange =
        props.validateOnChange === undefined ? false : props.validateOnChange
    const showCancelButton =
        props.showCancelButton === undefined ? true : props.showCancelButton
    const showButtons =
        props.showButtons === undefined ? true : props.showButtons
    const showErrorMessage =
        props.showErrorMessage === undefined ? true : props.showErrorMessage

    const initialValues = props.initialValues || {}

    const isModalForm: boolean = modal || false
    // let fBag

    const handleCancel = (formikHelpers: FormikHelpers<FormValues>) => {
        if (isModalForm) {
            hideForm(formikHelpers)
        } else {
            onCancel && onCancel()
            resetForm(formikHelpers)
        }
    }

    /**
     * Close the modal form.
     */
    const hideForm = (formikHelpers: FormikHelpers<FormValues>) => {
        if (hide !== undefined) {
            hide()
            resetForm(formikHelpers)
        }
    }

    const resetForm = (formikHelpers: FormikHelpers<FormValues>) => {
        setTimeout(function () {
            formikHelpers.resetForm()
        }, 200)
    }

    /**
     * Submit the form
     */
    const onSubmit = (
        values: FormValues,
        actions: FormikHelpers<FormValues>
    ) => {
        if (submit) {
            submit(values)
                .then(() => {
                    successText && message.success(successText, 1.5)
                    if (modal && hide) {
                        hide()
                    }
                    actions.resetForm()
                })
                .catch((error: Error) => {
                    showErrorMessage && message.error(error.message, 1.5)
                    actions.setFieldError('general', error.message)
                })
                .finally(() => {
                    actions.setSubmitting(false)
                })
        }
    }

    const validate = (values: FormValues) => {
        setFormValues(values)
    }

    const getFormikBag = (formikBag: any) => {
        props.getFormikBag && props.getFormikBag(formikBag)
    }

    useEffect(() => {
        getFormValues && getFormValues(formValues)
    }, [formValues])

    const formikProps = {
        enableReinitialize: true,
        initialValues,
        // onReset: onResetForm,
        onSubmit,
        validate,
        validationSchema
    }
    /**
     * Render either a form or a button that will show the form in a modal.
     */

    return (
        <React.Fragment>
            <Formik validateOnChange={validateOnChange} {...formikProps}>
                {(formikBag: FormikProps<FormValues>) => {
                    getFormikBag(formikBag)
                    const { handleSubmit, isSubmitting, resetForm } = formikBag
                    const formikHelpers: FormikHelpers<FormValues> = formikBag
                    const buttons = (
                        <Buttons
                            formikBag={formikBag}
                            // @ts-ignore
                            getButtons={getButtons}
                            resetButtonName={resetButtonName}
                            showButtons={showButtons}
                            showCancelButton={showCancelButton}
                            submitButtonName={submitButtonName}
                            handleSubmit={handleSubmit}
                            handleCancel={() => handleCancel(formikHelpers)}
                        />
                    )

                    const css = `.${styles.form} .ant-modal-content { border-left: .8rem solid ${formSideColor};}`

                    // ant-modal-content
                    return isModalForm ? (
                        <React.Fragment>
                            <style>{css}</style>
                            <Modal
                                onCancel={() => handleCancel(formikHelpers)}
                                className={`${styles.form} ${modalFormClassName}`}
                                destroyOnClose={true}
                                footer={buttons}
                                {...modalProps}
                                title={formTitle || undefined}
                                visible={isVisible}
                            >
                                <div className={formContainerClassName}>
                                    <div className={formClassName}>
                                        <FormTemplate
                                            form={form}
                                            isSubmitting={isSubmitting}
                                        />
                                    </div>
                                    {props.children}
                                </div>
                            </Modal>
                        </React.Fragment>
                    ) : (
                        <div className={formContainerClassName}>
                            <div className={formClassName}>
                                <FormTemplate
                                    form={form}
                                    formTitle={formTitle || undefined}
                                    isSubmitting={isSubmitting}
                                    buttons={buttons}
                                />
                            </div>
                            {props.children}
                        </div>
                    )
                }}
            </Formik>
        </React.Fragment>
    )
})
